import { CompanyId, getPortalContext, isLocalHostname, normalizeCompanyId } from '@/lib/portal';

export type TechnicianRole = 'technician' | 'lead_tech' | 'manager';

export interface TechnicianProfile {
  id: string;
  company_id: CompanyId;
  successware_employee_code: string | null;
  full_name: string;
  email: string;
  role: TechnicianRole;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface TechProfileResponse {
  success: boolean;
  technician?: TechnicianProfile;
  company_id?: CompanyId | null;
  error?: string;
  message?: string;
}

export type TechContestGoalType = 'first_to' | 'most_sales' | 'highest_rate';
export type TechContestMetricKey = 'sales' | 'close_rate';

export interface TechContest {
  id: string;
  company_id: CompanyId;
  name: string;
  description: string | null;
  start_date: string;
  end_date: string;
  goal_type: TechContestGoalType;
  goal_value: number | null;
  prize_description: string | null;
  prize_amount: number | null;
  winner_tech_id: string | null;
  is_active: boolean;
  created_at: string | null;
}

export interface TechContestTopRow {
  rank: number;
  technician: {
    id: string;
    full_name: string;
    role: TechnicianRole;
  };
  value: number | null;
}

export interface TechContestSummary extends TechContest {
  metric_key: TechContestMetricKey;
  my: {
    value: number | null;
    rank: number | null;
  };
  leader: {
    technician_id: string;
    full_name: string | null;
    value: number | null;
  } | null;
  top: TechContestTopRow[];
}

export interface TechContestsResponse {
  success: boolean;
  company_id?: CompanyId | null;
  contests?: TechContestSummary[];
  error?: string;
  message?: string;
}

export interface TechContestStandingsRow {
  rank: number;
  technician: TechnicianProfile;
  value: number | null;
  presentations: number | null;
  sold: number | null;
  reached_at: string | null;
}

export interface TechContestStandingsResponse {
  success: boolean;
  company_id?: CompanyId | null;
  contest?: TechContest;
  metric_key?: TechContestMetricKey;
  standings?: TechContestStandingsRow[];
  error?: string;
  message?: string;
}

export interface TechInternalMembershipSummary {
  id: string;
  membership_number: string | null;
  customer_first_name: string | null;
  customer_last_name: string | null;
  customer_email: string | null;
  customer_phone: string | null;
  customer_address: string | null;
  customer_city: string | null;
  customer_state: string | null;
  customer_zip: string | null;
  plan_name: string | null;
  status: string | null;
  start_date: string | null;
  end_date: string | null;
  next_billing_date: string | null;
  billing_frequency: string | null;
  systems_count: number | null;
  tune_ups_per_year: number | null;
  visits_used: number | null;
  total_visits_allowed: number | null;
  cancelled_at: string | null;
}

export interface TechSpiffHistoryRow {
  id: string;
  spiff_type: string;
  amount_cents: number;
  status: string;
  earned_at: string;
  paid_at: string | null;
  pay_period: string | null;
  notes: string | null;
  membership_id: string | null;
  membership_sale_id: string | null;
  membership?: TechInternalMembershipSummary | null;
  customer_name?: string | null;
}

export interface TechSpiffProjectionBucket {
  count: number;
  amount_cents: number;
}

export interface TechSpiffUpcomingRenewal {
  membership_id: string;
  membership_number: string | null;
  customer_name: string | null;
  plan_name: string | null;
  status: string | null;
  next_renewal_date: string;
  days_until: number;
  at_risk: boolean;
  next_billing_date: string | null;
}

export interface TechSpiffsResponse {
  success: boolean;
  company_id?: CompanyId | null;
  totals?: {
    ytd: TechSpiffTotals;
    all_time: TechSpiffTotals;
  };
  history?: TechSpiffHistoryRow[];
  projections?: {
    next_30_days: TechSpiffProjectionBucket;
    next_60_days: TechSpiffProjectionBucket;
    next_90_days: TechSpiffProjectionBucket;
    next_365_days: TechSpiffProjectionBucket;
    upcoming_renewals: TechSpiffUpcomingRenewal[];
    at_risk_renewals: TechSpiffUpcomingRenewal[];
    active_memberships: number;
    base_goal: {
      goal: number;
      active: number;
      remaining: number;
    };
  };
  error?: string;
  message?: string;
}

export type TechPortfolioFilter = 'all' | 'active' | 'at_risk' | 'cancelled';
export type TechPortfolioSort = 'newest' | 'renewal' | 'alpha';

export interface TechPortfolioSummary {
  total: number;
  active: number;
  at_risk: number;
  cancelled: number;
  retention_rate: number;
}

export interface TechPortfolioRow {
  membership_id: string;
  membership_number: string | null;
  sold_at: string | null;
  sale_type: string | null;
  customer_name: string | null;
  customer_address: string | null;
  plan_name: string | null;
  systems_count: number | null;
  tune_ups_per_year: number | null;
  status: string | null;
  start_date: string | null;
  next_billing_date: string | null;
  next_renewal_date: string | null;
  days_until_renewal: number | null;
  at_risk: boolean;
  is_cancelled: boolean;
  billing_frequency: string | null;
  visits_used: number | null;
  total_visits_allowed: number | null;
  cancelled_at: string | null;
}

export interface TechPortfolioResponse {
  success: boolean;
  company_id?: CompanyId | null;
  summary?: TechPortfolioSummary;
  rows?: TechPortfolioRow[];
  error?: string;
  message?: string;
}

export interface TechPortfolioQuery {
  filter?: TechPortfolioFilter;
  sort?: TechPortfolioSort;
}

export type TechLeaderboardRange = 'weekly' | 'monthly' | 'ytd';

export interface TechLeaderboardRow {
  technician: TechnicianProfile;
  memberships_sold: number;
  sale_type_counts: TechSalesBreakdown;
  presentations: number;
  presentations_sold: number;
  close_rate: number | null;
  retention: {
    total: number;
    active: number;
    rate: number | null;
  };
}

export interface TechLeaderboardResponse {
  success: boolean;
  company_id?: CompanyId | null;
  range?: {
    key: TechLeaderboardRange;
    start: string;
    end: string;
  };
  rows?: TechLeaderboardRow[];
  error?: string;
  message?: string;
}

export interface TechBadge {
  badge_type: string;
  title: string;
  description: string;
  earned: boolean;
}

export interface TechBadgesResponse {
  success: boolean;
  company_id?: CompanyId | null;
  badges?: TechBadge[];
  stats?: {
    lifetime_sales: number;
    lifetime_presentations: number;
    lifetime_close_rate: number;
    retention_total: number;
    retention_active: number;
    retention_rate: number;
    weekly_sales: number;
  };
  error?: string;
  message?: string;
}

export interface TechDashboardResponse {
  success: boolean;
  company_id?: CompanyId | null;
  technician?: TechnicianProfile;
  stats?: {
    today: {
      memberships_sold: number;
    };
    ytd: {
      memberships_sold: number;
      spiffs: {
        pending_cents: number;
        paid_cents: number;
        cancelled_cents: number;
      };
    };
  };
  recent_sales?: Array<{
    id: string;
    sold_at: string;
    sale_type: string;
    membership?: {
      id: string;
      membership_number: string | null;
      customer_first_name: string | null;
      customer_last_name: string | null;
      plan_name: string | null;
      status: string | null;
    } | null;
  }>;
  error?: string;
  message?: string;
}

export type TechCurrentAssignmentSource = 'scheduled_calls' | 'assignments';

export type TechCurrentAssignmentKind = 'current' | 'next';

export type SuccesswareJobDetail = Record<string, unknown>;

export interface TechCurrentAssignment {
  source: TechCurrentAssignmentSource;
  kind?: TechCurrentAssignmentKind;
  job: {
    id: string | null;
    number: string | null;
    class: string | null;
    type: string | null;
    status: string | null;
  };
  customer?:
    | {
        first_name: string | null;
        last_name: string | null;
      }
    | null;
  location:
    | {
        id: string | null;
        address1: string | null;
        address2: string | null;
        city: string | null;
        state: string | null;
        zipCode: string | null;
      }
    | null;
  contact:
    | {
        phone: string | null;
        email: string | null;
      }
    | null;
  timestamps: {
    scheduled_for?: string | null;
    scheduled_for_local?: string | null;
    time_preference?: string | null;
    notified_at: string | null;
    dispatched_at: string | null;
    onsite_at: string | null;
    completed_at: string | null;
  };
  job_details?: SuccesswareJobDetail | null;
}

export interface TechCurrentAssignmentResponse {
  success: boolean;
  company_id?: CompanyId | null;
  employee_code?: string | null;
  current_assignment?: TechCurrentAssignment | null;
  warnings?: string[];
  error?: string;
  message?: string;
}

export interface TechAdminTechniciansResponse {
  success: boolean;
  technicians?: TechnicianProfile[];
  error?: string;
  message?: string;
}

export interface TechAdminCreateTechnicianPayload {
  email: string;
  full_name: string;
  role?: TechnicianRole;
  successware_employee_code?: string | null;
}

export interface TechAdminCreateTechnicianResponse {
  success: boolean;
  technician?: TechnicianProfile;
  temp_password?: string;
  error?: string;
  message?: string;
}

export interface TechAdminUpdateTechnicianPayload {
  is_active?: boolean;
  role?: TechnicianRole;
  full_name?: string;
  successware_employee_code?: string | null;
}

export interface TechAdminUpdateTechnicianResponse {
  success: boolean;
  technician?: TechnicianProfile;
  error?: string;
  message?: string;
}

export interface TechAdminSetTechnicianPasswordPayload {
  password?: string | null;
}

export interface TechAdminSetTechnicianPasswordResponse {
  success: boolean;
  technician_id?: string;
  temp_password?: string;
  error?: string;
  message?: string;
}

export type TechPerformanceRange = 'today' | 'last7' | 'last30' | 'ytd';

export interface TechSalesBreakdown {
  new: number;
  upgrade: number;
  renewal: number;
}

export interface TechSpiffTotals {
  pending_cents: number;
  paid_cents: number;
  cancelled_cents: number;
}

export interface TechAdminPerformanceRow {
  technician: TechnicianProfile;
  memberships_sold: number;
  sale_type_counts: TechSalesBreakdown;
  spiffs: TechSpiffTotals;
}

export interface TechAdminPerformanceResponse {
  success: boolean;
  company_id?: CompanyId | null;
  range?: {
    key: TechPerformanceRange;
    start: string;
    end: string;
  };
  summary?: {
    memberships_sold: number;
    sale_type_counts: TechSalesBreakdown;
    spiffs: TechSpiffTotals;
  };
  rows?: TechAdminPerformanceRow[];
  error?: string;
  message?: string;
}

function getJsonErrorMessage(payload: unknown): string | null {
  if (!payload || typeof payload !== 'object') return null;

  const obj = payload as Record<string, unknown>;
  if (typeof obj.error === 'string' && obj.error.trim()) return obj.error;
  if (typeof obj.message === 'string' && obj.message.trim()) return obj.message;

  return null;
}

function buildTechUrl(path: string): string {
  const base = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
  const url = new URL(path, base);

  const portal = getPortalContext();
  if (
    portal.portal === 'tech' &&
    portal.companyId &&
    isLocalHostname(portal.hostname) &&
    !url.searchParams.get('company_id')
  ) {
    url.searchParams.set('company_id', portal.companyId);
  }

  return url.toString();
}

async function requestJson<T>(path: string, options: RequestInit & { accessToken?: string }): Promise<T> {
  const url = buildTechUrl(path);

  const { accessToken, ...fetchOptions } = options;

  const headers = new Headers(fetchOptions.headers || {});
  if (accessToken) {
    headers.set('Authorization', `Bearer ${accessToken}`);
  }
  if (!headers.has('Content-Type') && fetchOptions.body) {
    headers.set('Content-Type', 'application/json');
  }

  const method = String(fetchOptions.method || 'GET').toUpperCase();
  if (method === 'GET') {
    // Avoid 304s / empty bodies from browser caching; this client expects a JSON body.
    fetchOptions.cache = 'no-store';
  }

  const res = await fetch(url, { ...fetchOptions, headers });

  const rawText = await res.text();
  let json: unknown = null;
  if (rawText) {
    try {
      json = JSON.parse(rawText) as unknown;
    } catch {
      json = null;
    }
  }

  if (!res.ok) {
    const msg = getJsonErrorMessage(json);
    throw new Error(msg || `Request failed (${res.status})`);
  }

  return json as T;
}

export async function getTechProfile(
  accessToken: string
): Promise<{ technician: TechnicianProfile; company_id: CompanyId | null }> {
  const res = await requestJson<TechProfileResponse>('/api/tech/profile', {
    method: 'GET',
    accessToken,
  });

  if (!res.success || !res.technician) {
    throw new Error(res.error || 'Failed to load technician profile.');
  }

  const companyId = res.company_id ?? normalizeCompanyId(res.technician.company_id) ?? null;
  return { technician: res.technician, company_id: companyId };
}

export async function getTechDashboard(accessToken: string): Promise<TechDashboardResponse> {
  const res = await requestJson<TechDashboardResponse>('/api/tech/dashboard', {
    method: 'GET',
    accessToken,
  });

  if (!res.success) {
    throw new Error(res.error || 'Failed to load technician dashboard.');
  }

  return res;
}

export async function getTechCurrentAssignment(accessToken: string): Promise<TechCurrentAssignmentResponse> {
  const res = await requestJson<TechCurrentAssignmentResponse>('/api/tech/current-assignment', {
    method: 'GET',
    accessToken,
  });

  if (!res.success) {
    throw new Error(res.error || 'Failed to load current assignment.');
  }

  return res;
}

// ---------------------------------------------------------------------------
// Schedule + Jobs (Field app MVP)
// ---------------------------------------------------------------------------

export interface TechScheduleRow {
  assignment_id: string | null;
  job: {
    id: string | null;
    number: string | null;
    class: string | null;
    type: string | null;
    status: string | null;
  };
  customer:
    | {
        first_name: string | null;
        last_name: string | null;
      }
    | null;
  location:
    | {
        id: string | null;
        address1: string | null;
        address2: string | null;
        city: string | null;
        state: string | null;
        zipCode: string | null;
      }
    | null;
  contact:
    | {
        phone: string | null;
        email: string | null;
      }
    | null;
  timestamps: {
    scheduled_for: string | null;
    scheduled_for_local: string | null;
    time_preference: string | null;
    notified_at: string | null;
    dispatched_at: string | null;
    onsite_at: string | null;
    completed_at: string | null;
  };
}

export interface TechScheduleResponse {
  success: boolean;
  company_id?: CompanyId | null;
  employee_code?: string | null;
  date?: string | null;
  date_thru?: string | null;
  rows?: TechScheduleRow[];
  warnings?: string[];
  error?: string;
  message?: string;
}

export interface TechScheduleQuery {
  date?: string;
  dateThru?: string;
}

export async function getTechSchedule(accessToken: string, query: TechScheduleQuery): Promise<TechScheduleResponse> {
  const params = new URLSearchParams();
  if (query.date) params.set('date', query.date);
  if (query.dateThru) params.set('dateThru', query.dateThru);

  const suffix = params.toString();
  const path = suffix ? `/api/tech/schedule?${suffix}` : '/api/tech/schedule';

  const res = await requestJson<TechScheduleResponse>(path, {
    method: 'GET',
    accessToken,
  });

  if (!res.success) {
    throw new Error(res.error || 'Failed to load schedule.');
  }

  return res;
}

export interface TechJobDetailResponse {
  success: boolean;
  company_id?: CompanyId | null;
  employee_code?: string | null;
  date?: string | null;
  schedule_row?: unknown;
  job_details?: unknown;
  warnings?: string[];
  error?: string;
  message?: string;
}

export async function getTechJobDetail(
  accessToken: string,
  jobId: string,
  query: { date?: string } = {}
): Promise<TechJobDetailResponse> {
  const params = new URLSearchParams();
  if (query.date) params.set('date', query.date);

  const suffix = params.toString();
  const path = suffix
    ? `/api/tech/jobs/${encodeURIComponent(jobId)}?${suffix}`
    : `/api/tech/jobs/${encodeURIComponent(jobId)}`;

  const res = await requestJson<TechJobDetailResponse>(path, {
    method: 'GET',
    accessToken,
  });

  if (!res.success) {
    throw new Error(res.error || 'Failed to load job.');
  }

  return res;
}

export type TechJobEventType = 'en_route' | 'on_site' | 'depart';

export interface TechJobEventResponse {
  success: boolean;
  company_id?: CompanyId | null;
  job_id?: string;
  assignment_id?: string;
  event_type?: string;
  assignment_updated?: boolean;
  error?: string;
  message?: string;
}

export async function createTechJobEvent(
  accessToken: string,
  jobId: string,
  eventType: TechJobEventType,
  options: { assignmentId?: string | null; date?: string | null } = {}
): Promise<TechJobEventResponse> {
  const params = new URLSearchParams();
  if (options.date) params.set('date', options.date);

  const suffix = params.toString();
  const path = suffix
    ? `/api/tech/jobs/${encodeURIComponent(jobId)}/events?${suffix}`
    : `/api/tech/jobs/${encodeURIComponent(jobId)}/events`;

  const body: Record<string, unknown> = { event_type: eventType };
  if (options.assignmentId) body.assignment_id = options.assignmentId;

  const res = await requestJson<TechJobEventResponse>(path, {
    method: 'POST',
    accessToken,
    body: JSON.stringify(body),
  });

  if (!res.success) {
    throw new Error(res.error || 'Failed to write job event.');
  }

  return res;
}

// ---------------------------------------------------------------------------
// HVAC Diagnostic Platform (Phase 1 MVP)
// ---------------------------------------------------------------------------

export type TechDiagnosticSessionStatus = 'in_progress' | 'completed' | 'abandoned' | 'hard_stop';

export interface TechDiagnosticSession {
  id: string;
  company_id: CompanyId;
  technician_id: string;

  successware_job_id: string | null;
  job_number: string | null;
  location_id: string | null;

  status: TechDiagnosticSessionStatus;
  started_at: string;
  completed_at: string | null;

  current_step: string | null;
  diagnostic_pathway: string | null;

  job_details: Record<string, unknown>;
  equipment: Record<string, unknown>;

  pre_visit_data: Record<string, unknown>;
  safety_checks: Record<string, unknown>;
  safety_gate_passed: boolean;
  interview_responses: Record<string, unknown>;
  baseline_data: Record<string, unknown>;

  ai_analysis: Record<string, unknown>;
  diagnosis: Record<string, unknown>;
  recommendations: Record<string, unknown>;
  repair_replace_analysis: Record<string, unknown>;

  upgrades_presented: unknown[];
  upgrades_accepted: unknown[];
  replacement_presented: boolean;
  replacement_quoted: boolean;

  options_presented: Record<string, unknown>;
  option_selected: string | null;

  report_generated_at: string | null;
  report_url: string | null;

  customer_signed_at: string | null;
  customer_signature: Record<string, unknown>;

  created_at: string;
  updated_at: string;
}

export interface TechDiagnosticTestResult {
  id: string;
  session_id: string;
  test_module_id: string;
  started_at: string | null;
  completed_at: string | null;
  inputs: Record<string, unknown>;
  calculations: Record<string, unknown>;
  status: string | null;
  interpretation: Record<string, unknown>;
  ai_explanation: string | null;
  photos: unknown[];
  tech_notes: string | null;
  upgrade_opportunities: unknown[];
  created_at: string;
}

export interface TechCreateDiagnosticSessionPayload {
  job_number?: string | null;
  successware_job_id?: string | null;
  location_id?: string | null;
  diagnostic_pathway?: string | null;
  job_details?: Record<string, unknown>;
  pre_visit_data?: Record<string, unknown>;
}

export interface TechUpdateDiagnosticSessionPayload {
  status?: TechDiagnosticSessionStatus;
  current_step?: string | null;
  diagnostic_pathway?: string | null;

  job_details?: Record<string, unknown>;
  equipment?: Record<string, unknown>;
  pre_visit_data?: Record<string, unknown>;
  safety_checks?: Record<string, unknown>;
  safety_gate_passed?: boolean;
  interview_responses?: Record<string, unknown>;
  baseline_data?: Record<string, unknown>;

  ai_analysis?: Record<string, unknown>;
  diagnosis?: Record<string, unknown>;
  recommendations?: Record<string, unknown>;
  repair_replace_analysis?: Record<string, unknown>;

  upgrades_presented?: unknown[];
  upgrades_accepted?: unknown[];
  replacement_presented?: boolean;
  replacement_quoted?: boolean;

  options_presented?: Record<string, unknown>;
  option_selected?: string | null;

  report_generated_at?: string | null;
  report_url?: string | null;

  customer_signed_at?: string | null;
  customer_signature?: Record<string, unknown>;

  completed_at?: string | null;
}

export interface TechDiagnosticSessionResponse {
  success: boolean;
  session?: TechDiagnosticSession;
  error?: string;
  message?: string;
}

export interface TechDiagnosticSessionsResponse {
  success: boolean;
  sessions?: TechDiagnosticSession[];
  error?: string;
  message?: string;
}

export async function createTechDiagnosticSession(
  accessToken: string,
  payload: TechCreateDiagnosticSessionPayload
): Promise<TechDiagnosticSession> {
  const res = await requestJson<TechDiagnosticSessionResponse>('/api/tech/diagnostics', {
    method: 'POST',
    accessToken,
    body: JSON.stringify(payload || {}),
  });

  if (!res.success || !res.session) {
    throw new Error(res.error || 'Failed to create diagnostic session.');
  }

  return res.session;
}

export async function listTechDiagnosticSessions(accessToken: string): Promise<TechDiagnosticSession[]> {
  const res = await requestJson<TechDiagnosticSessionsResponse>('/api/tech/diagnostics', {
    method: 'GET',
    accessToken,
  });

  if (!res.success) {
    throw new Error(res.error || 'Failed to load diagnostic sessions.');
  }

  return res.sessions || [];
}

export async function getTechDiagnosticSession(accessToken: string, sessionId: string): Promise<TechDiagnosticSession> {
  const res = await requestJson<TechDiagnosticSessionResponse>(
    `/api/tech/diagnostics/${encodeURIComponent(sessionId)}`,
    {
      method: 'GET',
      accessToken,
    }
  );

  if (!res.success || !res.session) {
    throw new Error(res.error || 'Failed to load diagnostic session.');
  }

  return res.session;
}

export async function updateTechDiagnosticSession(
  accessToken: string,
  sessionId: string,
  payload: TechUpdateDiagnosticSessionPayload
): Promise<TechDiagnosticSession> {
  const res = await requestJson<TechDiagnosticSessionResponse>(
    `/api/tech/diagnostics/${encodeURIComponent(sessionId)}`,
    {
      method: 'PATCH',
      accessToken,
      body: JSON.stringify(payload || {}),
    }
  );

  if (!res.success || !res.session) {
    throw new Error(res.error || 'Failed to update diagnostic session.');
  }

  return res.session;
}

export interface TechDiagnosticTestResultsResponse {
  success: boolean;
  test_results?: TechDiagnosticTestResult[];
  error?: string;
  message?: string;
}

export interface TechCreateDiagnosticTestResultPayload {
  test_module_id: string;
  inputs?: Record<string, unknown>;
  calculations?: Record<string, unknown>;
  interpretation?: Record<string, unknown>;
  status?: string | null;
  ai_explanation?: string | null;
  photos?: unknown[];
  tech_notes?: string | null;
  upgrade_opportunities?: unknown[];
}

export interface TechDiagnosticTestResultResponse {
  success: boolean;
  test_result?: TechDiagnosticTestResult;
  error?: string;
  message?: string;
}

export async function listTechDiagnosticTestResults(
  accessToken: string,
  sessionId: string
): Promise<TechDiagnosticTestResult[]> {
  const res = await requestJson<TechDiagnosticTestResultsResponse>(
    `/api/tech/diagnostics/${encodeURIComponent(sessionId)}/test-results`,
    {
      method: 'GET',
      accessToken,
    }
  );

  if (!res.success) {
    throw new Error(res.error || 'Failed to load diagnostic test results.');
  }

  return res.test_results || [];
}

export async function createTechDiagnosticTestResult(
  accessToken: string,
  sessionId: string,
  payload: TechCreateDiagnosticTestResultPayload
): Promise<TechDiagnosticTestResult> {
  const res = await requestJson<TechDiagnosticTestResultResponse>(
    `/api/tech/diagnostics/${encodeURIComponent(sessionId)}/test-results`,
    {
      method: 'POST',
      accessToken,
      body: JSON.stringify(payload || {}),
    }
  );

  if (!res.success || !res.test_result) {
    throw new Error(res.error || 'Failed to save diagnostic test result.');
  }

  return res.test_result;
}

export type TechDiagnosticInvoiceStatus = 'draft' | 'paid' | 'void' | 'payment_failed';

export interface TechDiagnosticInvoice {
  id: string;
  company_id: CompanyId;
  diagnostic_session_id: string;
  invoice_number: string | null;
  status: TechDiagnosticInvoiceStatus;
  amount_due_today_cents: number;
  currency: string;
  option_selected: string | null;
  option_snapshot: Record<string, unknown>;
  customer_email: string | null;
  customer_name: string | null;
  customer_phone: string | null;
  stax_customer_id: string | null;
  stax_payment_method_id: string | null;
  stax_transaction_id: string | null;
  paid_at: string | null;
  email_sent_at: string | null;
  email_message_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface TechDiagnosticInvoiceResponse {
  success: boolean;
  invoice?: TechDiagnosticInvoice;
  charge?: unknown;
  email?: unknown;
  error?: string;
  message?: string;
}

export interface TechPayDiagnosticInvoicePayload {
  payment_token: string;
  customer: {
    email: string;
    first_name: string;
    last_name: string;
    phone?: string;
    address_1?: string;
    address_city?: string;
    address_state?: string;
    address_zip?: string;
  };
}

export async function getTechDiagnosticInvoice(
  accessToken: string,
  sessionId: string
): Promise<TechDiagnosticInvoice | null> {
  const res = await requestJson<TechDiagnosticInvoiceResponse>(
    `/api/tech/diagnostics/${encodeURIComponent(sessionId)}/invoice`,
    {
      method: 'GET',
      accessToken,
    }
  );

  if (!res.success) {
    throw new Error(res.error || 'Failed to load invoice.');
  }

  return res.invoice || null;
}

export async function createTechDiagnosticInvoice(
  accessToken: string,
  sessionId: string
): Promise<TechDiagnosticInvoice> {
  const res = await requestJson<TechDiagnosticInvoiceResponse>(
    `/api/tech/diagnostics/${encodeURIComponent(sessionId)}/invoice`,
    {
      method: 'POST',
      accessToken,
      body: JSON.stringify({}),
    }
  );

  if (!res.success || !res.invoice) {
    throw new Error(res.error || 'Failed to create invoice.');
  }

  return res.invoice;
}

export async function payTechDiagnosticInvoice(
  accessToken: string,
  sessionId: string,
  payload: TechPayDiagnosticInvoicePayload
): Promise<{ invoice: TechDiagnosticInvoice; charge?: unknown; email?: unknown }> {
  const res = await requestJson<TechDiagnosticInvoiceResponse>(
    `/api/tech/diagnostics/${encodeURIComponent(sessionId)}/invoice/pay`,
    {
      method: 'POST',
      accessToken,
      body: JSON.stringify(payload || {}),
    }
  );

  if (!res.success || !res.invoice) {
    throw new Error(res.error || 'Failed to process payment.');
  }

  return {
    invoice: res.invoice,
    charge: res.charge,
    email: res.email,
  };
}

export interface TechAdminCreateContestPayload {
  name: string;
  description?: string | null;
  start_date: string;
  end_date: string;
  goal_type: TechContestGoalType;
  goal_value?: number | null;
  prize_description?: string | null;
  prize_amount?: number | null;
  is_active?: boolean;
}

export interface TechAdminUpdateContestPayload {
  name?: string;
  description?: string | null;
  start_date?: string;
  end_date?: string;
  goal_type?: TechContestGoalType;
  goal_value?: number | null;
  prize_description?: string | null;
  prize_amount?: number | null;
  winner_tech_id?: string | null;
  is_active?: boolean;
}

export interface TechAdminContestsResponse {
  success: boolean;
  company_id?: CompanyId | null;
  contests?: TechContest[];
  error?: string;
  message?: string;
}

export interface TechAdminContestResponse {
  success: boolean;
  contest?: TechContest;
  error?: string;
  message?: string;
}

export async function listAdminContests(accessToken: string): Promise<TechContest[]> {
  const res = await requestJson<TechAdminContestsResponse>('/api/tech/admin/contests', {
    method: 'GET',
    accessToken,
  });

  if (!res.success) {
    throw new Error(res.error || 'Failed to load contests.');
  }

  return res.contests || [];
}

export async function createAdminContest(accessToken: string, payload: TechAdminCreateContestPayload): Promise<TechContest> {
  const res = await requestJson<TechAdminContestResponse>('/api/tech/admin/contests', {
    method: 'POST',
    accessToken,
    body: JSON.stringify(payload),
  });

  if (!res.success || !res.contest) {
    throw new Error(res.error || 'Failed to create contest.');
  }

  return res.contest;
}

export async function updateAdminContest(
  accessToken: string,
  contestId: string,
  payload: TechAdminUpdateContestPayload
): Promise<TechContest> {
  const res = await requestJson<TechAdminContestResponse>(
    `/api/tech/admin/contests/${encodeURIComponent(contestId)}`,
    {
      method: 'PATCH',
      accessToken,
      body: JSON.stringify(payload),
    }
  );

  if (!res.success || !res.contest) {
    throw new Error(res.error || 'Failed to update contest.');
  }

  return res.contest;
}

export async function getTechContests(accessToken: string): Promise<TechContestsResponse> {
  const res = await requestJson<TechContestsResponse>('/api/tech/contests', {
    method: 'GET',
    accessToken,
  });

  if (!res.success) {
    throw new Error(res.error || 'Failed to load contests.');
  }

  return res;
}

export async function getTechContestStandings(
  accessToken: string,
  contestId: string
): Promise<TechContestStandingsResponse> {
  const res = await requestJson<TechContestStandingsResponse>(
    `/api/tech/contests/${encodeURIComponent(contestId)}/standings`,
    {
      method: 'GET',
      accessToken,
    }
  );

  if (!res.success) {
    throw new Error(res.error || 'Failed to load contest standings.');
  }

  return res;
}

export async function getTechSpiffs(accessToken: string): Promise<TechSpiffsResponse> {
  const res = await requestJson<TechSpiffsResponse>('/api/tech/spiffs', {
    method: 'GET',
    accessToken,
  });

  if (!res.success) {
    throw new Error(res.error || 'Failed to load spiffs.');
  }

  return res;
}

export async function getTechPortfolio(accessToken: string, query: TechPortfolioQuery): Promise<TechPortfolioResponse> {
  const params = new URLSearchParams();
  if (query.filter) params.set('filter', query.filter);
  if (query.sort) params.set('sort', query.sort);

  const suffix = params.toString();
  const path = suffix ? `/api/tech/portfolio?${suffix}` : '/api/tech/portfolio';

  const res = await requestJson<TechPortfolioResponse>(path, {
    method: 'GET',
    accessToken,
  });

  if (!res.success) {
    throw new Error(res.error || 'Failed to load customer portfolio.');
  }

  return res;
}

export async function getTechLeaderboard(
  accessToken: string,
  range: TechLeaderboardRange
): Promise<TechLeaderboardResponse> {
  const res = await requestJson<TechLeaderboardResponse>(
    `/api/tech/leaderboard?range=${encodeURIComponent(range)}`,
    {
      method: 'GET',
      accessToken,
    }
  );

  if (!res.success) {
    throw new Error(res.error || 'Failed to load leaderboard.');
  }

  return res;
}

export async function getTechBadges(accessToken: string): Promise<TechBadgesResponse> {
  const res = await requestJson<TechBadgesResponse>('/api/tech/badges', {
    method: 'GET',
    accessToken,
  });

  if (!res.success) {
    throw new Error(res.error || 'Failed to load badges.');
  }

  return res;
}

export type TechPastCallJobDetailMode = 'summary' | 'full';

export interface TechPastCallJobDetailResponse {
  success: boolean;
  company_id?: CompanyId | null;
  jobId?: string;
  mode?: TechPastCallJobDetailMode;
  detail?: unknown;
  error?: string;
  message?: string;
}

export async function getTechPastCallJobDetail(
  accessToken: string,
  jobId: string,
  mode: TechPastCallJobDetailMode
): Promise<TechPastCallJobDetailResponse> {
  const params = new URLSearchParams();
  params.set('jobId', jobId);
  params.set('mode', mode);

  const res = await requestJson<TechPastCallJobDetailResponse>(
    `/api/tech/past-calls/job-detail?${params.toString()}`,
    {
      method: 'GET',
      accessToken,
    }
  );

  if (!res.success) {
    throw new Error(res.error || 'Failed to load job detail.');
  }

  return res;
}

export type TechMembershipBadgeStatus = 'member' | 'non_member';
export type TechMembershipBadgeSource = 'successware' | 'internal' | null;

export interface TechMembershipBadge {
  status: TechMembershipBadgeStatus;
  source: TechMembershipBadgeSource;
}

export interface TechPastCallsRow {
  scheduledForLocal: string | null;
  jobId: string | null;
  jobNumber: string | null;
  jobType: string | null;
  jobClass: string | null;
  customerName: string | null;
  address: string | null;
  locationId: string | null;
  membershipAtTime: TechMembershipBadge;
  membershipNow: TechMembershipBadge;
  agreementsSoldOnJob: {
    count: number;
    codes: string[];
  };
  opportunity: boolean;
}

export interface TechPastCallsSummary {
  totalPastCalls: number;
  eligibleOpportunities: number;
  agreementsSold: number;
  conversionPct: number;
  lostOpportunities: number;
  spiffEarnedUsd: number;
  spiffLostUsd: number;
}

export interface TechPastCallsResponse {
  success: boolean;
  company_id?: CompanyId | null;
  range?: {
    start: string;
    end: string;
    days: number;
  } | null;
  summary?: TechPastCallsSummary;
  rows?: TechPastCallsRow[];
  warnings?: string[];
  error?: string;
  message?: string;
}

export interface TechPastCallsQuery {
  start?: string;
  end?: string;
  days?: number;
  page?: number;
  size?: number;
}

export async function getTechPastCalls(
  accessToken: string,
  query: TechPastCallsQuery
): Promise<TechPastCallsResponse> {
  const params = new URLSearchParams();
  if (query.start) params.set('start', query.start);
  if (query.end) params.set('end', query.end);
  if (query.days !== undefined) params.set('days', String(query.days));
  if (query.page !== undefined) params.set('page', String(query.page));
  if (query.size !== undefined) params.set('size', String(query.size));

  const suffix = params.toString();
  const path = suffix ? `/api/tech/past-calls?${suffix}` : '/api/tech/past-calls';

  const res = await requestJson<TechPastCallsResponse>(path, {
    method: 'GET',
    accessToken,
  });

  if (!res.success) {
    throw new Error(res.error || 'Failed to load past calls.');
  }

  return res;
}

export type TechPresentationOutcome = 'sold' | 'declined' | 'abandoned';

export interface TechPresentationSession {
  id: string;
  company_id: CompanyId;
  technician_id: string;
  customer_id: string | null;
  location_id: string | null;
  started_at: string;
  completed_at: string | null;
  outcome: TechPresentationOutcome | null;
  membership_id: string | null;
  steps_completed: string[];
  decline_reason: string | null;
  device_info: unknown;
  created_at: string;
  updated_at: string;
}

export interface TechStartPresentationPayload {
  customer_id?: string | null;
  location_id?: string | null;
  device_info?: Record<string, unknown>;
}

export interface TechStartPresentationResponse {
  success: boolean;
  session?: TechPresentationSession;
  error?: string;
  message?: string;
}

export interface TechUpdatePresentationPayload {
  customer_id?: string | null;
  location_id?: string | null;
  steps_completed?: string[];
  outcome?: TechPresentationOutcome | null;
  membership_id?: string | null;
  decline_reason?: string | null;
  completed_at?: string | null;
  device_info?: Record<string, unknown>;
}

export interface TechUpdatePresentationResponse {
  success: boolean;
  session?: TechPresentationSession;
  error?: string;
  message?: string;
}

export async function startTechPresentation(
  accessToken: string,
  payload: TechStartPresentationPayload
): Promise<TechPresentationSession> {
  const res = await requestJson<TechStartPresentationResponse>('/api/tech/presentations', {
    method: 'POST',
    accessToken,
    body: JSON.stringify(payload),
  });

  if (!res.success || !res.session) {
    throw new Error(res.error || 'Failed to start presentation.');
  }

  return res.session;
}

export async function updateTechPresentation(
  accessToken: string,
  presentationId: string,
  payload: TechUpdatePresentationPayload
): Promise<TechPresentationSession> {
  const res = await requestJson<TechUpdatePresentationResponse>(
    `/api/tech/presentations/${encodeURIComponent(presentationId)}`,
    {
      method: 'PATCH',
      accessToken,
      body: JSON.stringify(payload),
    }
  );

  if (!res.success || !res.session) {
    throw new Error(res.error || 'Failed to update presentation.');
  }

  return res.session;
}

export async function listAdminTechnicians(accessToken: string): Promise<TechnicianProfile[]> {
  const res = await requestJson<TechAdminTechniciansResponse>('/api/tech/admin/technicians', {
    method: 'GET',
    accessToken,
  });

  if (!res.success) {
    throw new Error(res.error || 'Failed to load technicians.');
  }

  return res.technicians || [];
}

export async function createAdminTechnician(
  accessToken: string,
  payload: TechAdminCreateTechnicianPayload
): Promise<{ technician: TechnicianProfile; tempPassword: string }> {
  const res = await requestJson<TechAdminCreateTechnicianResponse>('/api/tech/admin/technicians', {
    method: 'POST',
    accessToken,
    body: JSON.stringify(payload),
  });

  if (!res.success || !res.technician || !res.temp_password) {
    throw new Error(res.error || 'Failed to create technician.');
  }

  return { technician: res.technician, tempPassword: res.temp_password };
}

export async function updateAdminTechnician(
  accessToken: string,
  technicianId: string,
  payload: TechAdminUpdateTechnicianPayload
): Promise<TechnicianProfile> {
  const res = await requestJson<TechAdminUpdateTechnicianResponse>(
    `/api/tech/admin/technicians/${encodeURIComponent(technicianId)}`,
    {
      method: 'PATCH',
      accessToken,
      body: JSON.stringify(payload),
    }
  );

  if (!res.success || !res.technician) {
    throw new Error(res.error || 'Failed to update technician.');
  }

  return res.technician;
}

export async function setAdminTechnicianPassword(
  accessToken: string,
  technicianId: string,
  payload: TechAdminSetTechnicianPasswordPayload
): Promise<string> {
  const res = await requestJson<TechAdminSetTechnicianPasswordResponse>(
    `/api/tech/admin/technicians/${encodeURIComponent(technicianId)}/password`,
    {
      method: 'POST',
      accessToken,
      body: JSON.stringify(payload || {}),
    }
  );

  if (!res.success || !res.temp_password) {
    throw new Error(res.error || 'Failed to set technician password.');
  }

  return res.temp_password;
}

export async function getAdminPerformance(
  accessToken: string,
  range: TechPerformanceRange
): Promise<TechAdminPerformanceResponse> {
  const res = await requestJson<TechAdminPerformanceResponse>(
    `/api/tech/admin/performance?range=${encodeURIComponent(range)}`,
    {
      method: 'GET',
      accessToken,
    }
  );

  if (!res.success) {
    throw new Error(res.error || 'Failed to load performance metrics.');
  }

  return res;
}
